'use client'

import ImageWithFallback from '@/components/ui/ImageWithFallback'
import { IMAGES_BACKEND_URL } from '@/utils/constants'
import { IconArrowRight } from '@tabler/icons-react'
import { motion, useMotionValue, useReducedMotion, useSpring } from 'framer-motion'
import Link from 'next/link'
import { FC, useEffect, useState } from 'react'
import { createPortal } from 'react-dom'

export type ProjectIndexItem = {
  id: string
  name: string
  slug: string
  main_image?: string
  summary?: string
}

const projectImageSrc = (project: ProjectIndexItem) => (project.main_image ? `${IMAGES_BACKEND_URL}projects/${project.id}/${project.main_image}` : '/images/mocks/mock-bg.jpg')

type ProjectIndexProps = {
  projects: ProjectIndexItem[]
  basePath: string
}

// Typografischer Projekt-Index: große Namenszeilen mit Outline-Nummern und
// Kurzbeschreibung. Auf Geräten mit Maus folgt eine Bildvorschau dem Cursor,
// auf Touch-Geräten zeigt jede Zeile ein Thumbnail. Respektiert prefers-reduced-motion.
const ProjectIndex: FC<ProjectIndexProps> = ({ projects, basePath }) => {
  const [activeIndex, setActiveIndex] = useState<number | null>(null)
  // Portal erst nach dem Mount: document.body existiert nicht beim Server-Rendering
  const [mounted, setMounted] = useState(false)
  const reduceMotion = useReducedMotion()

  const cursorX = useMotionValue(0)
  const cursorY = useMotionValue(0)
  const previewX = useSpring(cursorX, { stiffness: 150, damping: 20 })
  const previewY = useSpring(cursorY, { stiffness: 150, damping: 20 })

  useEffect(() => {
    setMounted(true)
  }, [])

  const handleMouseMove = (e: React.MouseEvent) => {
    cursorX.set(e.clientX + 32)
    cursorY.set(e.clientY - 120)
  }

  const active = activeIndex !== null ? projects[activeIndex] : null

  return (
    <div onMouseMove={reduceMotion ? undefined : handleMouseMove} onMouseLeave={() => setActiveIndex(null)}>
      <ul className="border-t border-dashed border-accent">
        {projects.map((project, index) => (
          <li key={project.id} className="border-b border-dashed border-accent">
            <Link
              href={`${basePath}/${project.slug}`}
              className="group grid grid-cols-[auto_1fr_auto] lg:grid-cols-[5rem_1fr_minmax(0,24rem)_auto] items-center gap-6 lg:gap-12 py-6 lg:py-10"
              onMouseEnter={() => setActiveIndex(index)}
              onFocus={() => setActiveIndex(index)}
              onBlur={() => setActiveIndex(null)}
            >
              <span
                aria-hidden
                className="text-2xl lg:text-[2.75rem] leading-none font-bold text-transparent select-none tabular-nums transition-opacity duration-300 opacity-60 group-hover:opacity-100"
                style={{ WebkitTextStroke: '1px var(--color-accent)' }}
              >
                {String(index + 1).padStart(2, '0')}
              </span>
              <span className="flex flex-row items-center gap-6 min-w-0">
                <span className="text-white text-2xl lg:text-6xl font-medium leading-none tracking-tight truncate transition-all duration-300 group-hover:text-accent lg:group-hover:translate-x-3">
                  {project.name}
                </span>
                {/* Thumbnail für Touch-Geräte / kleine Viewports */}
                <span className="relative lg:hidden shrink-0 ml-auto w-24 h-16 rounded-lg overflow-hidden">
                  <ImageWithFallback fill sizes="6rem" className="object-cover" src={projectImageSrc(project)} fallbackSrc="/images/mocks/mock-bg.jpg" alt="" />
                </span>
              </span>
              <span className="hidden lg:block">
                {project.summary && <span className="line-clamp-2 text-KMRS-Light text-base font-light leading-6 transition-colors duration-300 group-hover:text-white">{project.summary}</span>}
              </span>
              <IconArrowRight
                size={36}
                aria-hidden
                className="hidden lg:block text-accent opacity-0 -translate-x-3 transition-all duration-300 group-hover:opacity-100 group-hover:translate-x-0"
              />
            </Link>
          </li>
        ))}
      </ul>

      {/* Schwebende Bildvorschau, nur Desktop mit Maus. Per Portal in den body,
          damit position:fixed nicht an transformierten Vorfahren (motion-Wrapper) ankert. */}
      {mounted &&
        !reduceMotion &&
        createPortal(
          <motion.div
            aria-hidden
            className="hidden lg:block fixed top-0 left-0 z-50 pointer-events-none w-[26rem] h-[17rem] rounded-2xl overflow-hidden shadow-2xl"
            style={{ x: previewX, y: previewY }}
            animate={{ opacity: active ? 1 : 0, scale: active ? 1 : 0.92 }}
            transition={{ duration: 0.25, ease: 'easeOut' }}
          >
            {projects.map((project, index) => (
              <ImageWithFallback
                key={project.id}
                fill
                sizes="26rem"
                className={`object-cover transition-opacity duration-300 ${activeIndex === index ? 'opacity-100' : 'opacity-0'}`}
                src={projectImageSrc(project)}
                fallbackSrc="/images/mocks/mock-bg.jpg"
                alt=""
              />
            ))}
          </motion.div>,
          document.body
        )}
    </div>
  )
}

export default ProjectIndex
